import { useContext, useEffect, useState } from 'react';
import {
  MenuItem,
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  Select,
  TextField,
  Typography,
  InputAdornment,
  SelectChangeEvent,
  IconButton,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import STAKEHOLDER_OPTIONS, {
  StakeHolderId,
} from '@pages/onboarding/Mexico/constants/stakeHolderOptions';
import {
  eighteenYearsAgo,
  onlyNumbersRegex,
  onlyTextRegex,
} from '@pages/onboarding/USA/constants';
import { formatUpperNoAccents, validateSpaces } from '@utils/validations';
import clsx from 'clsx';
import GENDER_OPTIONS from '@pages/onboarding/Mexico/constants/genderOptions';
import { MexicoStakeHoldersFormData } from '@pages/onboarding/Mexico/types/stakeholders.type';
import useEntityType from '@pages/onboarding/Mexico/hooks/useEntityType';
import { CountriesList, FederalEntitiesList } from '@efex-services/auth/types';
import CURP_REGEX from '@constants/curp';
import useEffectOnce from '@main-hooks/useEffectOnce';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Visibility from '@mui/icons-material/Visibility';
import { Tooltip } from '@efexteam/components';
import StakeHolderContext from '../context/StakeHolderContext';
import CompanySectorAutocomplete from '../../components/CompanySectorAutocomplete';
import { SSN_LENGTH } from '../stakeholders.constants';

interface PersonalInfoProps {
  data?: MexicoStakeHoldersFormData & {
    businessSector?: {
      id: number;
    };
  };
  getCountries: (value: string) => void;
  birthcountries: CountriesList[];
  getBirthCountries: (value: string) => void;
  federalEntities: FederalEntitiesList[];
  getFederalEntities: (value: string) => void;
  countries: CountriesList[];
}

const COUNTRY_CODE_USA = 229;

const PersonalInfo = ({
  data,
  getCountries,
  birthcountries,
  getBirthCountries,
  federalEntities,
  getFederalEntities,
  countries,
}: PersonalInfoProps) => {
  const { t } = useTranslation();

  const [countryList, setCountryList] = useState<CountriesList[]>([]);
  const [dynamicTexts, setDynamicTexts] = useState({
    resourceStatement: t('regResourceStatement', { name: '', lastName: '' }),
    resourceStatementFamily: t('regResourceStatementFamily', {
      name: '',
      lastName: '',
    }),
  });
  const [isResourceStatament, setIsResourceStatament] = useState<boolean>(
    data?.resourceFamilyStatament ?? false
  );
  const [isResourceFamilyStatament, setIsResourceFamilyStatament] =
    useState<boolean>(data?.resourceFamilyStatament ?? false);
  const [userNationalityValue, setUserNationalityValue] = useState<{
    id: number | undefined;
    name: string;
    countryKey: string;
  } | null>(null);
  const [nationalityInputValue, setNationalityInputValue] = useState('');

  const { personType, setPersonType, businessSectorId, setBusinessSectorId } =
    useContext(StakeHolderContext);

  const {
    isNaturalMX,
    isNaturalPerson,
    getEntityTypeSelectOption,
    isNaturalFR,
  } = useEntityType({ personType });

  const {
    register,
    control,
    formState: { errors },
    setValue,
    watch,
  } = useFormContext<MexicoStakeHoldersFormData>();

  const [nameField, lastNameField, nationalityCountryId] = watch([
    'name',
    'lastName',
    'nationalityCountryId',
  ]);

  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleEntityTypeChange = (event: SelectChangeEvent<unknown>) => {
    const selectedOption = STAKEHOLDER_OPTIONS.find(
      (option) => option.key === event.target.value
    );

    if (selectedOption) {
      setPersonType(selectedOption.id);
      setValue('entityTypeId', selectedOption.id);
    }
  };

  const handleBusinessSectorId = (companySector?: ICompanySector) => {
    if (!companySector) return;

    setValue('businessSectorId', companySector.id);
    setBusinessSectorId(companySector);
  };

  useEffect(() => {
    const currentName = nameField;
    const currentLastName = lastNameField;

    const nameLastNameParams =
      currentName && currentLastName
        ? { name: currentName, lastName: currentLastName }
        : { name: '', lastName: '' };

    setDynamicTexts({
      resourceStatement: t('regResourceStatement', nameLastNameParams),
      resourceStatementFamily: t(
        'regResourceStatementFamily',
        nameLastNameParams
      ),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameField, lastNameField]);

  useEffectOnce(() => {
    if (data?.countryBirthId) {
      getBirthCountries(data.countryBirthId);
    }

    if (data?.nationalityCountryId) {
      getCountries(data.nationalityCountryId);
    } else {
      setUserNationalityValue(null);
      setNationalityInputValue('');
    }
  });

  useEffectOnce(() => {
    if (data?.legalRepresentativeStatament) {
      setIsResourceStatament(data.legalRepresentativeStatament);
    }

    if (data?.resourceFamilyStatament) {
      setIsResourceFamilyStatament(data.resourceFamilyStatament);
    }
  });

  useEffect(() => {
    if (!countries) return;

    if (countries.length === 0) return;

    if (data?.nationalityCountryId && !userNationalityValue) {
      const foundCountry = countries.find(
        (country) =>
          country.id.toString() === data.nationalityCountryId?.toString()
      );
      if (foundCountry) {
        setUserNationalityValue(foundCountry);
        setNationalityInputValue(foundCountry.name);
      }
    }

    setCountryList(countries);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countries]);

  useEffect(() => {
    if (data?.entityTypeId) {
      setPersonType(data.entityTypeId);
      setValue('entityTypeId', data.entityTypeId);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.entityTypeId]);

  return (
    <>
      <Grid item xs={12}>
        <FormControl fullWidth>
          <Typography variant="subtitle2">
            {t('regStakeholderEntity')}
          </Typography>
          <Controller
            name="entityTypeId"
            rules={{ required: true }}
            control={control}
            render={({ field }) => (
              <Select
                labelId="entityTypeId"
                id="entityTypeId"
                label={t('regStakeholderEntity')}
                error={!!errors.entityTypeId}
                {...register('entityTypeId', {
                  required: true,
                })}
                value={
                  getEntityTypeSelectOption(
                    field.value ?? StakeHolderId.NATURAL_MX
                  ).key
                }
                onChange={handleEntityTypeChange}
                disabled={!!data?.id}
              >
                {STAKEHOLDER_OPTIONS.map((stakeholderOption) => (
                  <MenuItem
                    key={stakeholderOption.key}
                    value={stakeholderOption.key}
                  >
                    {t(stakeholderOption.value)}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
          {errors.entityTypeId && (
            <p className="text-orange-600 text-xs">
              {t('regStakeHolderEntityValidation')}
            </p>
          )}
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <Typography variant="subtitle2">
            {isNaturalPerson ? t(`onbStHlName`) : t(`onbCompanyName`)}
          </Typography>
          <TextField
            autoComplete="off"
            type="text"
            className="custom-textfield"
            placeholder={t(`onbStHlName`) as string}
            helperText={t(`onbStHlNameMessage`) as string}
            error={!!errors.name}
            {...register('name', {
              required: true,
              pattern: {
                value: onlyTextRegex,
                message: '',
              },
              validate: {
                spaces: (value) => validateSpaces(value, t),
              },
              onChange: (e) => {
                const { value } = e.target;
                e.target.value = formatUpperNoAccents(value);
              },
            })}
          />
          {errors && errors.name && errors.name.type === 'required' && (
            <p className="text-orange-600 text-xs mt-1">{t('defaultError')}</p>
          )}
          {errors && errors.name && errors.name.type === 'pattern' && (
            <p className="text-orange-600 text-xs mt-1">
              {t('onbOnlyTextError')}
            </p>
          )}
          {errors && errors.name && errors.name.type === 'spaces' && (
            <p className="text-orange-600 text-xs mt-1">
              {errors.name.message}
            </p>
          )}
        </FormControl>
      </Grid>
      {isNaturalPerson && (
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <Typography variant="subtitle2">{t(`onbStHlLastName`)}</Typography>
            <TextField
              autoComplete="off"
              type="text"
              className="custom-textfield "
              placeholder={t(`onbStHlLastName`) as string}
              helperText={t(`onbStHlNameMessage`) as string}
              error={!!errors.lastName}
              {...register('lastName', {
                required: true,
                pattern: {
                  value: onlyTextRegex,
                  message: '',
                },
                validate: {
                  spaces: (value) => validateSpaces(value, t),
                },
                onChange: (e) => {
                  const { value } = e.target;
                  e.target.value = formatUpperNoAccents(value);
                },
              })}
            />
            {errors &&
              errors.lastName &&
              errors.lastName.type === 'required' && (
                <p className="text-orange-600 text-xs mt-1">
                  {t('defaultError')}
                </p>
              )}
            {errors &&
              errors.lastName &&
              errors.lastName.type === 'pattern' && (
                <p className="text-orange-600 text-xs mt-1">
                  {t('onbOnlyTextError')}
                </p>
              )}
            {errors && errors.lastName && errors.lastName.type === 'spaces' && (
              <p className="text-orange-600 text-xs mt-1">
                {errors.lastName.message}
              </p>
            )}
          </FormControl>
        </Grid>
      )}
      {isNaturalPerson && (
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <Typography variant="subtitle2">
              {t(`onbStHlSecondLastName`)}
            </Typography>
            <TextField
              autoComplete="off"
              type="text"
              className="custom-textfield "
              placeholder={t(`onbStHlSecondLastName`) as string}
              helperText={t(`onbStHlNameMessage`) as string}
              error={!!errors.lastName}
              {...register('secondLastName', {
                required: false,
                pattern: {
                  value: onlyTextRegex,
                  message: '',
                },
                validate: {
                  spaces: (value) => validateSpaces(value, t),
                },
                onChange: (e) => {
                  const { value } = e.target;
                  e.target.value = formatUpperNoAccents(value);
                },
              })}
            />
            {errors &&
              errors.lastName &&
              errors.lastName.type === 'required' && (
                <p className="text-orange-600 text-xs mt-1">
                  {t('defaultError')}
                </p>
              )}
            {errors &&
              errors.lastName &&
              errors.lastName.type === 'pattern' && (
                <p className="text-orange-600 text-xs mt-1">
                  {t('onbOnlyTextError')}
                </p>
              )}
            {errors && errors.lastName && errors.lastName.type === 'spaces' && (
              <p className="text-orange-600 text-xs mt-1">
                {errors.lastName.message}
              </p>
            )}
          </FormControl>
        </Grid>
      )}
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <Typography variant="subtitle2">
            {isNaturalPerson ? t(`onbStHlBirthDate`) : t(`onbConstitutionDate`)}
          </Typography>
          <Controller
            name="birthDate"
            rules={{ required: true }}
            control={control}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterLuxon}>
                <DatePicker
                  {...field}
                  disableFuture
                  maxDate={
                    isNaturalPerson
                      ? DateTime.fromJSDate(eighteenYearsAgo)
                      : undefined
                  }
                  value={
                    field.value
                      ? DateTime.fromISO(field.value.toString())
                      : null
                  }
                  className={clsx('w-full ')}
                  onChange={(value) => {
                    if (!value) return;
                    field.onChange(value);
                  }}
                  slotProps={{
                    // eslint-disable-next-line react/no-unstable-nested-components
                    textField: (params) => (
                      <TextField
                        {...params}
                        error={!!errors.birthDate}
                        fullWidth
                      />
                    ),
                  }}
                />
              </LocalizationProvider>
            )}
          />
          {errors && errors.birthDate && (
            <p className="text-orange-600 text-xs mt-1">{t('defaultError')}</p>
          )}
        </FormControl>
      </Grid>
      {isNaturalPerson && (
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <Typography variant="subtitle2">{t('regBirthCountry')}</Typography>
            <Controller
              name="countryBirthId"
              rules={{ required: true }}
              control={control}
              render={({ field: { onChange, value, ...props } }) => (
                <Autocomplete
                  {...props}
                  disablePortal
                  getOptionLabel={(option) => option.name}
                  options={birthcountries ?? []}
                  value={
                    birthcountries?.find(
                      (country) => country.id.toString() === value?.toString()
                    ) ?? null
                  }
                  onChange={(_, newValue) => {
                    if (newValue) {
                      onChange(newValue.id.toString());
                    } else {
                      onChange('');
                    }
                  }}
                  onInputChange={(_, newInputValue, reason) => {
                    if (reason === 'input') {
                      getBirthCountries(newInputValue);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!errors.countryBirthId}
                      className="custom-textfield"
                    />
                  )}
                  selectOnFocus
                  handleHomeEndKeys
                  clearOnBlur
                  freeSolo={false}
                />
              )}
            />
            {errors?.countryBirthId && (
              <p className="text-orange-600 text-xs mt-1">
                {t('defaultError')}
              </p>
            )}
          </FormControl>
        </Grid>
      )}
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <Typography variant="subtitle2">{t(`onbStHlPercentage`)}</Typography>
          <TextField
            autoComplete="off"
            type="number"
            className="custom-textfield "
            placeholder="50"
            error={!!errors.percentage}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            {...register('percentage', {
              required: true,
              pattern: {
                value: onlyNumbersRegex,
                message: '',
              },
              max: 100,
            })}
          />
          {errors &&
            errors.percentage &&
            errors.percentage.type === 'required' && (
              <p className="text-orange-600 text-xs mt-1">
                {t('defaultError')}
              </p>
            )}
          {errors &&
            errors.percentage &&
            errors.percentage.type === 'pattern' && (
              <p className="text-orange-600 text-xs mt-1">
                {t('onbOnlyNumberError')}
              </p>
            )}
          {errors && errors.percentage && errors.percentage.type === 'max' && (
            <p className="text-orange-600 text-xs mt-1">
              {t('onbPercentageError')}
            </p>
          )}
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <Typography variant="subtitle2">
            {isNaturalPerson
              ? t(`regNationality`)
              : t(`regIncorporationCountry`)}
          </Typography>
          <Controller
            name="nationalityCountryId"
            rules={{ required: true }}
            control={control}
            render={({ field: { onChange, value, ...props } }) => (
              <Autocomplete
                {...props}
                disablePortal
                getOptionLabel={(nationality) => nationality?.name || ''}
                options={countryList || []}
                value={userNationalityValue}
                inputValue={nationalityInputValue}
                onInputChange={(_, newInputValue, reason) => {
                  if (reason === 'input') {
                    setNationalityInputValue(newInputValue);
                    getCountries(newInputValue);
                  } else if (reason === 'clear') {
                    setNationalityInputValue('');
                    setUserNationalityValue(null);
                    setValue('nationalityCountryId', '');
                    setValue('ssn', '');
                  }
                }}
                onChange={(_, selectedOption) => {
                  setUserNationalityValue(selectedOption);
                  setNationalityInputValue(selectedOption?.name || '');
                  setValue(
                    'nationalityCountryId',
                    selectedOption?.id?.toString() || ''
                  );
                  setValue('ssn', '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    error={!!errors.nationalityCountryId}
                    autoComplete="off"
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: 'off',
                    }}
                  />
                )}
              />
            )}
          />
          {errors.nationalityCountryId && (
            <p className="text-orange-600 text-xs">{t('defaultError')}</p>
          )}
        </FormControl>
      </Grid>

      {(isNaturalFR || isNaturalMX) &&
        Number(nationalityCountryId) === COUNTRY_CODE_USA && (
          <Grid item md={6} sm={12}>
            <FormControl fullWidth>
              <Box className="flex items-center gap-2">
                <Typography variant="subtitle2">{t(`onbSsn`)}</Typography>
                <Tooltip title={t('onbSsnTooltip')}>
                  <LockOutlinedIcon fontSize="small" />
                </Tooltip>
              </Box>

              <Box className="flex">
                <TextField
                  autoComplete="off"
                  type={showPassword ? 'text' : 'password'}
                  className="custom-textfield w-full"
                  size="small"
                  placeholder={t(`onbSsn`) as string}
                  error={!!errors.ssn}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                  {...register('ssn', {
                    required: true,
                    pattern: {
                      value: onlyNumbersRegex,
                      message: '',
                    },
                    maxLength: SSN_LENGTH,
                    minLength: SSN_LENGTH,
                  })}
                />
              </Box>

              {errors?.ssn?.type === 'required' && (
                <p className="text-orange-600 text-xs mt-1">
                  {t('defaultError')}
                </p>
              )}

              {(errors?.ssn?.type === 'maxLength' ||
                errors?.ssn?.type === 'minLength') && (
                <p className="text-orange-600 text-xs mt-1">
                  {t('onbMaxDigits', { digits: SSN_LENGTH })}
                </p>
              )}
            </FormControl>
          </Grid>
        )}

      {isNaturalPerson ? (
        <>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <Typography variant="subtitle2">{t(`onbStHlTitle`)}</Typography>
              <TextField
                autoComplete="off"
                type="text"
                className="custom-textfield "
                placeholder={t('onbStHlTitle') || ''}
                error={!!errors.title}
                {...register('title', {
                  required: true,
                  pattern: {
                    value: onlyTextRegex,
                    message: '',
                  },
                  validate: {
                    spaces: (value) => validateSpaces(value, t),
                  },
                  onChange: (e) => {
                    const { value } = e.target;
                    e.target.value = formatUpperNoAccents(value);
                  },
                })}
              />
              {errors && errors.title && errors.title.type === 'required' && (
                <p className="text-orange-600 text-xs mt-1">
                  {t('defaultError')}
                </p>
              )}
              {errors && errors.title && errors.title.type === 'pattern' && (
                <p className="text-orange-600 text-xs mt-1">
                  {t('onbOnlyTextError')}
                </p>
              )}
              {errors && errors.title && errors.title.type === 'spaces' && (
                <p className="text-orange-600 text-xs mt-1">
                  {errors.title.message}
                </p>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <Typography variant="subtitle2">{t('regGender')}</Typography>
              <Controller
                name="gender"
                rules={{ required: isNaturalPerson }}
                control={control}
                render={({ field }) => (
                  <Select
                    labelId="gender"
                    id="gender"
                    label={t('regGender')}
                    error={!!errors.gender}
                    value={field?.value || ''}
                    onChange={field.onChange}
                  >
                    {GENDER_OPTIONS.map((genderOption) => (
                      <MenuItem key={genderOption.key} value={genderOption.key}>
                        {t(genderOption.value)}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {errors.gender && (
                <p className="text-orange-600 text-xs">
                  {t('regGenderValidation')}
                </p>
              )}
            </FormControl>
          </Grid>

          {isNaturalMX && (
            <>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <Typography variant="subtitle2">
                    {t(`regFederalEntity`)}
                  </Typography>
                  <Controller
                    name="federalEntityId"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange, ...props } }) => (
                      <Autocomplete
                        {...props}
                        disablePortal
                        getOptionLabel={(contact) => contact.description}
                        options={federalEntities ?? []}
                        value={
                          props.value
                            ? federalEntities?.find(
                                (entity) => entity.id === Number(props.value)
                              )
                            : null
                        }
                        onChangeCapture={(e) =>
                          getFederalEntities(
                            (e as React.ChangeEvent<HTMLInputElement>).target
                              .value
                          )
                        }
                        onChange={(_, selectedOption) =>
                          onChange(selectedOption?.id)
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            error={!!errors.federalEntityId}
                            autoComplete="off"
                            inputProps={{
                              ...params.inputProps,
                              autoComplete: 'off',
                            }}
                          />
                        )}
                        renderOption={(innerProps, option) => (
                          <Box
                            component="li"
                            className="px-3"
                            sx={{
                              '& > img': { mr: 2, flexShrink: 0 },
                            }}
                            {...innerProps}
                          >
                            <div>{option.description}</div>
                          </Box>
                        )}
                      />
                    )}
                  />
                  {errors.federalEntityId && (
                    <p className="text-orange-600 text-xs">
                      {t('defaultError')}
                    </p>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <Typography variant="subtitle2">
                    {t(`regCurp`)}
                    <a
                      href="https://www.gob.mx/curp/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-1 text-blue-600 hover:text-blue-800"
                    >
                      <span className="text-xs">({t('regCurpText')})</span>
                    </a>
                  </Typography>
                  <TextField
                    id="curp"
                    helperText={t('regCurpHelper')}
                    error={!!errors.curp}
                    {...register('curp', {
                      required: true,
                      pattern: CURP_REGEX,
                    })}
                    onChange={(event) => {
                      event.target.value = event.target.value.toUpperCase();
                    }}
                  />
                  {errors.curp && errors.curp.type === 'required' && (
                    <p className="text-orange-600 text-xs">
                      {t('defaultError')}
                    </p>
                  )}
                  {errors.curp && errors.curp.type === 'pattern' && (
                    <p className="text-orange-600 text-xs">
                      {t('regCurpValidation')}
                    </p>
                  )}
                </FormControl>
              </Grid>
            </>
          )}
        </>
      ) : null}
      <Grid item xs={12}>
        <FormControl fullWidth>
          <Typography variant="subtitle2">{t('regMxSector')}</Typography>
          <CompanySectorAutocomplete
            className="w-full"
            data={data}
            {...register('businessSectorId', {
              required: true,
            })}
            isError={errors && !!errors.businessSectorId}
            selectedValue={businessSectorId}
            showCompanySectorError={false}
            onSelectCompanySector={handleBusinessSectorId}
          />
          {errors && errors.businessSectorId && (
            <p className="text-orange-600 text-xs mt-1">{t('defaultError')}</p>
          )}
        </FormControl>
      </Grid>
      <Grid item xs={12} className="mx-5" />
      {isNaturalPerson && (
        <>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormControlLabel
                control={
                  <Controller
                    name="legalRepresentativeStatament"
                    control={control}
                    render={({ field: { value, ref, ...field } }) => (
                      <Checkbox
                        {...field}
                        onChange={(event, checked) => {
                          setIsResourceStatament(checked);
                          field.onChange(event);
                        }}
                        inputRef={ref}
                        checked={!!value}
                        disableRipple
                      />
                    )}
                  />
                }
                label={dynamicTexts.resourceStatement}
              />
            </FormControl>
          </Grid>
          {isResourceStatament ? (
            <Grid item xs={12} className="!mb-3">
              <FormControl fullWidth>
                <Typography variant="subtitle2">
                  {t('regResourceStatementResponse')}
                </Typography>
                <TextField
                  autoComplete="off"
                  type="text"
                  error={!!errors.legalRepresentativeResponse}
                  className="custom-textfield"
                  size="small"
                  placeholder={t('regResourceStatementResponse') as string}
                  {...register('legalRepresentativeResponse', {
                    required: true,
                  })}
                />
                {errors && errors.legalRepresentativeResponse && (
                  <p className="text-orange-600 text-xs mt-1">
                    {t('defaultError')}
                  </p>
                )}
              </FormControl>
            </Grid>
          ) : null}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormControlLabel
                control={
                  <Controller
                    name="resourceFamilyStatament"
                    control={control}
                    render={({ field: { value, ref, ...field } }) => (
                      <Checkbox
                        {...field}
                        onChange={(event, checked) => {
                          setIsResourceFamilyStatament(checked);
                          field.onChange(event);
                        }}
                        inputRef={ref}
                        checked={!!value}
                        disableRipple
                      />
                    )}
                  />
                }
                label={dynamicTexts.resourceStatementFamily}
              />{' '}
            </FormControl>
          </Grid>
          {isResourceFamilyStatament ? (
            <Grid item xs={12} className="!mb-3">
              <FormControl fullWidth>
                <Typography variant="subtitle2">
                  {t('regResourceStatementFamilyResponse')}
                </Typography>
                <TextField
                  autoComplete="off"
                  type="text"
                  error={!!errors.resourceFamilyResponse}
                  className="custom-textfield"
                  size="small"
                  placeholder={
                    t('regResourceStatementFamilyResponse') as string
                  }
                  {...register('resourceFamilyResponse', {
                    required: true,
                  })}
                />
                {errors && errors.resourceFamilyResponse && (
                  <p className="text-orange-600 text-xs mt-1">
                    {t('defaultError')}
                  </p>
                )}
              </FormControl>
            </Grid>
          ) : null}{' '}
        </>
      )}
    </>
  );
};

export default PersonalInfo;
